/**
 * SimilarStones custom element for displaying similar stones
 * Similar to similar-products but for stones (diamonds, moissanites, gemstones)
 * 
 * @class SimilarStones
 * @extends HTMLElement
 */
class SimilarStones extends HTMLElement {

  /**
   * Constructor for the SimilarStones element
   */
  constructor() {
    super();
    this.classList.add('hidden');
    this.stoneId = null;
    this.stoneType = null;
    this.relatedStones = [];
  }

  /**
   * Connected callback - called when element is added to DOM
   */
  async connectedCallback() {
    // Extract stone ID and type from URL
    this.extractStoneInfo();

    if (!this.stoneId || !this.stoneType) {
      console.error('SimilarStones: Could not determine stone ID or type from URL');
      return;
    }

    // Wait for stones API to be available (it's loaded with defer, so wait for DOMContentLoaded)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.waitForStonesAPI());
    } else {
      this.waitForStonesAPI();
    }
  }

  /**
   * Wait for stones API to be available, then load similar stones
   * @private
   */
  async waitForStonesAPI() {
    // Poll for stones API availability (max 50 attempts = 5 seconds)
    let attempts = 0;
    const maxAttempts = 50;

    const checkInterval = setInterval(() => {
      attempts++;
      
      if (window.theme?.stones) {
        clearInterval(checkInterval);
        this.loadSimilarStones();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('SimilarStones: Stones API failed to load within timeout period');
        this.classList.add('hidden');
      }
    }, 100);
  }

  /**
   * Extract stone ID and type from URL
   * Determines type based on URL path: /products/diamond, /products/moissanite, or /products/gemstone
   * @private
   */
  extractStoneInfo() {
    const url = new URL(window.location);
    const pathname = url.pathname.toLowerCase();
    const id = url.searchParams.get('id') || window.theme.stones.getIdFromSlug(url.searchParams.get('stone') || '');

    if (!id) {
      return;
    }

    this.stoneId = id;

    // Determine stone type from URL path
    if (pathname.includes('/products/diamond')) {
      this.stoneType = 'diamond';
    } else if (pathname.includes('/products/moissanite')) {
      this.stoneType = 'moissanite';
    } else if (pathname.includes('/products/gemstone')) {
      this.stoneType = 'gemstone';
    } else {
      console.warn('SimilarStones: Could not determine stone type from URL path:', pathname);
    }
  }

  /**
   * Fetch related stones from the API
   * @private
   */
  async loadSimilarStones() {
    if (!this.stoneId || !this.stoneType) {
      return;
    }

    try {
      // Call the API endpoint: /api/stones/{id}/related
      const apiUrl = `${window.theme.appUrl}/api/stones/${this.stoneId}/related`;
      
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle different response structures
      const stones = result.data || result || [];

      if (!Array.isArray(stones) || stones.length === 0) {
        // No similar stones found, hide the component
        this.classList.add('hidden');
        return;
      }

      this.relatedStones = stones;
      this.render();

    } catch (error) {
      console.error('Error loading similar stones:', error);
      // Hide component on error
      this.classList.add('hidden');
    }
  }

  /**
   * Render the similar stones slider
   * @private
   */
  render() {
    if (this.relatedStones.length === 0) {
      this.classList.add('hidden');
      return;
    }

    // Get title from data attribute or use default
    const title = this.getAttribute('data-title') || 'Similar Stones';

    // Create stone-slide element
    const stoneSlide = document.createElement('stone-slide');
    stoneSlide.setAttribute('data-title', title);

    // Add each stone as a card
    this.relatedStones.forEach(stone => {
      // Transform stone data if needed (similar to how getStoneById transforms it)
      const transformedStone = this.transformStoneData(stone);
      stoneSlide.addStoneCard(transformedStone, this.stoneType);
    });

    // Clear existing content and add the slider
    this.innerHTML = '';
    this.appendChild(stoneSlide);

    // Show the component with fade-in animation after a short delay
    // This ensures the stone-slide has time to initialize
    setTimeout(() => {
      this.classList.remove('hidden');
      setTimeout(() => {
        this.classList.remove('opacity-0');
      }, 100);
    }, 100);
  }

  /**
   * Transform stone data to match the format expected by stone-card
   * This ensures compatibility with the stone-card component
   * @param {Object} stone - Raw stone data from API
   * @returns {Object} Transformed stone data
   * @private
   */
  transformStoneData(stone) {
    // The API might return stones in different formats
    // Transform to match what stone-card expects
    return {
      id: stone.id || stone.gems_item_id || stone.stone_id,
      shape_id: stone.shape_id,
      carat: stone.carat,
      price_carat: stone.price_carat,
      total_price: stone.total_price,
      image_url: stone.image_url,
      color: stone.color,
      clarity: stone.clarity,
      cut: stone.cut,
      lab_grown: stone.lab_grown,
      gem_stone_type: stone.gem_stone_type,
      stone_type: stone.stone_type,
      gem_size: stone.gem_size,
      gem_grade: stone.gem_grade,
      moissanite_type: stone.moissanite_type,
      intensity: stone.intensity,
      report: stone.report,
      sku: stone.sku
    };
  }
}

// Register the web component
if (!customElements.get('similar-stones')) {
  customElements.define('similar-stones', SimilarStones);
}
